/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * CoreXLoader — Premium loading screen with 4 randomly-selected animation styles.
 *
 * Variants:
 *   - fullpage: fixed viewport takeover (Suspense fallback, route transitions)
 *   - overlay:  centered glassmorphic card with backdrop blur (in-page slow ops)
 *
 * Animations (randomly chosen unless forced via `animation` prop):
 *   1. Orbiting Rings   — rotating rings + halo pulse around logo
 *   2. Hex Grid         — breathing hex pattern behind logo
 *   3. Scan Reveal      — horizontal scan line reveals logo via clip-path
 *   4. Particle Convergence — orbiting dots converge toward logo
 *
 * All variants include an indeterminate progress bar at the bottom.
 */

import { useMemo } from 'react';
import { useTheme } from '@/lib/theme';
import { getLogo } from '@/config/project.config';

type AnimationStyle = 'orbiting-rings' | 'hex-grid' | 'scan-reveal' | 'particle-convergence';

interface CoreXLoaderProps {
  variant: 'fullpage' | 'overlay';
  animation?: AnimationStyle;
}

const ANIMATIONS: AnimationStyle[] = [
  'orbiting-rings',
  'hex-grid',
  'scan-reveal',
  'particle-convergence',
];

function pickRandom(): AnimationStyle {
  return ANIMATIONS[Math.floor(Math.random() * ANIMATIONS.length)];
}

/* ─── Orbiting Rings ─── */
function OrbitingRings() {
  return (
    <div className="cxl-anim cxl-orbiting">
      <div className="cxl-ring cxl-ring1" />
      <div className="cxl-ring cxl-ring2" />
      <div className="cxl-ring cxl-ring3" />
      <div className="cxl-halo cxl-halo1" />
      <div className="cxl-halo cxl-halo2" />
      <div className="cxl-ambient" />
    </div>
  );
}

/* ─── Hex Grid ─── */
function HexGrid() {
  // 7 hexagons arranged in a honeycomb ring
  const hexes = Array.from({ length: 7 }, (_, i) => i);
  return (
    <div className="cxl-anim cxl-hexgrid">
      <div className="cxl-hex-wrap">
        {hexes.map((i) => (
          <div key={i} className={`cxl-hex cxl-hex-${i}`}>
            <svg viewBox="0 0 100 87" preserveAspectRatio="xMidYMid meet">
              <polygon
                points="50,0 100,25 100,75 50,100 0,75 0,25"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              />
            </svg>
          </div>
        ))}
      </div>
      <div className="cxl-hex-glow" />
    </div>
  );
}

/* ─── Scan Reveal ─── */
function ScanReveal() {
  return (
    <div className="cxl-anim cxl-scanreveal">
      <div className="cxl-scan-line" />
      <div className="cxl-scan-mask" />
      <div className="cxl-scan-glow" />
    </div>
  );
}

/* ─── Particle Convergence ─── */
function ParticleConvergence() {
  const particles = Array.from({ length: 12 }, (_, i) => i);
  return (
    <div className="cxl-anim cxl-particles">
      {particles.map((i) => (
        <div key={i} className={`cxl-dot cxl-dot-${i}`} />
      ))}
      <div className="cxl-particle-glow" />
    </div>
  );
}

const ANIM_COMPONENTS: Record<AnimationStyle, () => JSX.Element> = {
  'orbiting-rings': OrbitingRings,
  'hex-grid': HexGrid,
  'scan-reveal': ScanReveal,
  'particle-convergence': ParticleConvergence,
};

export function CoreXLoader({ variant, animation }: CoreXLoaderProps) {
  const { isDark } = useTheme();
  const selected = useMemo(() => animation ?? pickRandom(), [animation]);
  const AnimComponent = ANIM_COMPONENTS[selected];

  const isFullpage = variant === 'fullpage';

  return (
    <div
      className={`cxl-root cxl-${variant}`}
      role="status"
      aria-label="Loading"
      aria-busy="true"
    >
      {isFullpage && <div className="cxl-bg" />}
      {!isFullpage && <div className="cxl-backdrop" />}

      <div className={isFullpage ? 'cxl-center' : 'cxl-card glow-border'}>
        <div className={isFullpage ? 'cxl-inner' : 'cxl-card-inner glass-card'}>
          {/* Logo + Animation wrapper */}
          <div className="cxl-logo-area">
            <AnimComponent />
            <img
              src={getLogo(isDark)}
              alt="CoreX"
              className="cxl-logo"
            />
          </div>

          {/* Progress bar */}
          <div className="cxl-progress">
            <div className="cxl-progress-bar" />
          </div>
        </div>
      </div>

      <style>{`
        /* ══════════════════════════════════════════════════════════════════
           COREX LOADER — Theme Variables (scoped)
           ══════════════════════════════════════════════════════════════════ */

        [data-theme="dark"] .cxl-root {
          --cxl-bg: #06080C;
          --cxl-accent: #E63946;
          --cxl-accent2: #C41E3A;
          --cxl-accent-dim: rgba(230, 57, 70, 0.15);
          --cxl-accent-glow: rgba(230, 57, 70, 0.25);
          --cxl-accent-faint: rgba(230, 57, 70, 0.06);
          --cxl-card-bg: rgba(16, 19, 26, 0.85);
          --cxl-card-border: rgba(255, 255, 255, 0.08);
          --cxl-text: #8A95AA;
          --cxl-ring: rgba(230, 57, 70, 0.3);
          --cxl-ring-dim: rgba(230, 57, 70, 0.08);
          --cxl-hex-color: rgba(230, 57, 70, 0.12);
          --cxl-dot-color: rgba(230, 57, 70, 0.6);
          --cxl-scan-color: #E63946;
          --cxl-backdrop: rgba(6, 8, 12, 0.75);
        }
        [data-theme="light"] .cxl-root {
          --cxl-bg: #EEF2F7;
          --cxl-accent: #1A5F7A;
          --cxl-accent2: #16526B;
          --cxl-accent-dim: rgba(26, 95, 122, 0.12);
          --cxl-accent-glow: rgba(26, 95, 122, 0.2);
          --cxl-accent-faint: rgba(26, 95, 122, 0.05);
          --cxl-card-bg: rgba(255, 255, 255, 0.92);
          --cxl-card-border: rgba(0, 0, 0, 0.08);
          --cxl-text: #4A5670;
          --cxl-ring: rgba(26, 95, 122, 0.25);
          --cxl-ring-dim: rgba(26, 95, 122, 0.06);
          --cxl-hex-color: rgba(26, 95, 122, 0.1);
          --cxl-dot-color: rgba(26, 95, 122, 0.5);
          --cxl-scan-color: #1A5F7A;
          --cxl-backdrop: rgba(238, 242, 247, 0.7);
        }

        /* ══ ROOT ══ */
        .cxl-root {
          z-index: 9999;
          font-family: 'Outfit', sans-serif;
        }

        /* ── Fullpage variant ── */
        .cxl-fullpage {
          position: fixed;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: cxl-fadeIn 0.3s ease both;
        }
        .cxl-bg {
          position: absolute;
          inset: 0;
          background: var(--cxl-bg);
        }
        .cxl-center {
          position: relative;
          z-index: 2;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 32px;
        }
        .cxl-inner {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 32px;
        }

        /* ── Overlay variant ── */
        .cxl-overlay {
          position: fixed;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: cxl-fadeIn 0.25s ease both;
        }
        .cxl-backdrop {
          position: absolute;
          inset: 0;
          background: var(--cxl-backdrop);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
        }
        .cxl-card {
          position: relative;
          z-index: 2;
          border-radius: 16px;
          min-width: 220px;
        }
        .cxl-card-inner {
          padding: 40px 48px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 28px;
          border-radius: 16px;
        }

        @keyframes cxl-fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        /* ══ LOGO ══ */
        .cxl-logo-area {
          position: relative;
          width: 120px;
          height: 120px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .cxl-overlay .cxl-logo-area {
          width: 80px;
          height: 80px;
        }
        .cxl-logo {
          width: 64px;
          height: 64px;
          object-fit: contain;
          position: relative;
          z-index: 5;
          filter: drop-shadow(0 0 12px var(--cxl-accent-dim));
        }
        .cxl-overlay .cxl-logo {
          width: 44px;
          height: 44px;
        }

        /* ══ PROGRESS BAR ══ */
        .cxl-progress {
          width: 140px;
          height: 2px;
          background: var(--cxl-accent-faint);
          border-radius: 2px;
          overflow: hidden;
          position: relative;
        }
        .cxl-overlay .cxl-progress {
          width: 100px;
        }
        .cxl-progress-bar {
          position: absolute;
          top: 0;
          left: -40%;
          width: 40%;
          height: 100%;
          background: linear-gradient(90deg, transparent, var(--cxl-accent), transparent);
          border-radius: 2px;
          animation: cxl-progressSlide 1.4s cubic-bezier(0.4, 0, 0.2, 1) infinite;
        }
        @keyframes cxl-progressSlide {
          0% { left: -40%; }
          100% { left: 100%; }
        }

        /* ══════════════════════════════════════════════════════════════════
           ANIMATION 1: ORBITING RINGS
           ══════════════════════════════════════════════════════════════════ */
        .cxl-orbiting {
          position: absolute;
          inset: 0;
        }
        .cxl-ring {
          position: absolute;
          border-radius: 50%;
          border-style: solid;
          border-color: var(--cxl-accent);
          top: 50%; left: 50%;
        }
        .cxl-ring1 {
          width: calc(100% + 16px);
          height: calc(100% + 16px);
          margin-top: calc(-50% - 8px);
          margin-left: calc(-50% - 8px);
          border-width: 1.5px;
          opacity: 0.25;
          animation: cxl-spin 8s linear infinite;
        }
        .cxl-ring2 {
          width: calc(100% + 36px);
          height: calc(100% + 36px);
          margin-top: calc(-50% - 18px);
          margin-left: calc(-50% - 18px);
          border-width: 1px;
          border-style: dashed;
          opacity: 0.12;
          animation: cxl-spin 14s linear infinite reverse;
        }
        .cxl-ring3 {
          width: calc(100% + 56px);
          height: calc(100% + 56px);
          margin-top: calc(-50% - 28px);
          margin-left: calc(-50% - 28px);
          border-width: 0.5px;
          border-style: dotted;
          opacity: 0.06;
          animation: cxl-spin 22s linear infinite;
        }
        @keyframes cxl-spin { to { transform: rotate(360deg); } }

        .cxl-halo {
          position: absolute;
          top: 50%; left: 50%;
          width: 80%;
          height: 80%;
          margin-top: -40%;
          margin-left: -40%;
          border-radius: 50%;
          border: 1px solid var(--cxl-accent);
          opacity: 0;
          pointer-events: none;
        }
        .cxl-halo1 { animation: cxl-haloPulse 2.5s ease-out infinite; }
        .cxl-halo2 { animation: cxl-haloPulse 2.5s ease-out 1.25s infinite; }
        @keyframes cxl-haloPulse {
          0% { opacity: 0.35; transform: scale(0.95); }
          100% { opacity: 0; transform: scale(1.6); }
        }

        .cxl-ambient {
          position: absolute;
          top: 50%; left: 50%;
          width: 100%;
          height: 100%;
          margin-top: -50%;
          margin-left: -50%;
          border-radius: 50%;
          background: radial-gradient(circle, var(--cxl-accent-glow), transparent 70%);
          animation: cxl-ambientPulse 3s ease-in-out infinite;
          pointer-events: none;
        }
        @keyframes cxl-ambientPulse {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.1); }
        }

        /* ══════════════════════════════════════════════════════════════════
           ANIMATION 2: HEX GRID
           ══════════════════════════════════════════════════════════════════ */
        .cxl-hexgrid {
          position: absolute;
          inset: -20px;
        }
        .cxl-hex-wrap {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .cxl-hex {
          position: absolute;
          width: 48px;
          height: 42px;
          color: var(--cxl-hex-color);
          opacity: 0;
          animation: cxl-hexBreathe 3s ease-in-out infinite;
        }
        .cxl-overlay .cxl-hex {
          width: 34px;
          height: 30px;
        }

        /* Center hex */
        .cxl-hex-0 { top: 50%; left: 50%; margin-top: -21px; margin-left: -24px; animation-delay: 0s; }
        /* Top */
        .cxl-hex-1 { top: 50%; left: 50%; margin-top: -63px; margin-left: -24px; animation-delay: 0.15s; }
        /* Top-right */
        .cxl-hex-2 { top: 50%; left: 50%; margin-top: -42px; margin-left: 12px; animation-delay: 0.3s; }
        /* Bottom-right */
        .cxl-hex-3 { top: 50%; left: 50%; margin-top: 0px; margin-left: 12px; animation-delay: 0.45s; }
        /* Bottom */
        .cxl-hex-4 { top: 50%; left: 50%; margin-top: 21px; margin-left: -24px; animation-delay: 0.6s; }
        /* Bottom-left */
        .cxl-hex-5 { top: 50%; left: 50%; margin-top: 0px; margin-left: -60px; animation-delay: 0.75s; }
        /* Top-left */
        .cxl-hex-6 { top: 50%; left: 50%; margin-top: -42px; margin-left: -60px; animation-delay: 0.9s; }

        @keyframes cxl-hexBreathe {
          0%, 100% { opacity: 0.15; }
          50% { opacity: 0.5; }
        }

        .cxl-hex-glow {
          position: absolute;
          top: 50%; left: 50%;
          width: 80%;
          height: 80%;
          margin-top: -40%;
          margin-left: -40%;
          border-radius: 50%;
          background: radial-gradient(circle, var(--cxl-accent-glow), transparent 70%);
          animation: cxl-ambientPulse 3.5s ease-in-out infinite;
          pointer-events: none;
        }

        /* ══════════════════════════════════════════════════════════════════
           ANIMATION 3: SCAN REVEAL
           ══════════════════════════════════════════════════════════════════ */
        .cxl-scanreveal {
          position: absolute;
          inset: 0;
        }
        .cxl-scan-line {
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent 5%, var(--cxl-scan-color) 50%, transparent 95%);
          opacity: 0.7;
          z-index: 10;
          animation: cxl-scanDown 2.2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
        }
        @keyframes cxl-scanDown {
          0% { top: -2px; opacity: 0.8; }
          80% { opacity: 0.6; }
          100% { top: calc(100% + 2px); opacity: 0; }
        }

        .cxl-scan-mask {
          position: absolute;
          inset: 0;
          border: 1px solid var(--cxl-ring);
          border-radius: 50%;
          opacity: 0;
          animation: cxl-scanPulse 2.2s ease-in-out infinite;
        }
        @keyframes cxl-scanPulse {
          0% { opacity: 0; transform: scale(0.9); }
          30% { opacity: 0.3; transform: scale(1); }
          70% { opacity: 0.3; transform: scale(1); }
          100% { opacity: 0; transform: scale(1.15); }
        }

        .cxl-scan-glow {
          position: absolute;
          top: 50%; left: 50%;
          width: 100%;
          height: 100%;
          margin-top: -50%;
          margin-left: -50%;
          border-radius: 50%;
          background: radial-gradient(circle, var(--cxl-accent-dim), transparent 60%);
          animation: cxl-scanGlowPulse 2.2s ease-in-out infinite;
          pointer-events: none;
        }
        @keyframes cxl-scanGlowPulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.7; }
        }

        /* ══════════════════════════════════════════════════════════════════
           ANIMATION 4: PARTICLE CONVERGENCE
           ══════════════════════════════════════════════════════════════════ */
        .cxl-particles {
          position: absolute;
          inset: 0;
        }
        .cxl-dot {
          position: absolute;
          width: 3px;
          height: 3px;
          border-radius: 50%;
          background: var(--cxl-dot-color);
          top: 50%;
          left: 50%;
          animation: cxl-orbit 4s linear infinite;
        }
        .cxl-overlay .cxl-dot {
          width: 2px;
          height: 2px;
        }

        /* Distribute 12 dots evenly around the orbit */
        .cxl-dot-0  { animation-delay: 0s;     --cxl-angle: 0deg;   --cxl-dist: 52px; --cxl-size: 3px; }
        .cxl-dot-1  { animation-delay: -0.33s;  --cxl-angle: 30deg;  --cxl-dist: 48px; --cxl-size: 2px; }
        .cxl-dot-2  { animation-delay: -0.66s;  --cxl-angle: 60deg;  --cxl-dist: 55px; --cxl-size: 2.5px; }
        .cxl-dot-3  { animation-delay: -1s;     --cxl-angle: 90deg;  --cxl-dist: 44px; --cxl-size: 3px; }
        .cxl-dot-4  { animation-delay: -1.33s;  --cxl-angle: 120deg; --cxl-dist: 50px; --cxl-size: 2px; }
        .cxl-dot-5  { animation-delay: -1.66s;  --cxl-angle: 150deg; --cxl-dist: 56px; --cxl-size: 3.5px; }
        .cxl-dot-6  { animation-delay: -2s;     --cxl-angle: 180deg; --cxl-dist: 46px; --cxl-size: 2px; }
        .cxl-dot-7  { animation-delay: -2.33s;  --cxl-angle: 210deg; --cxl-dist: 53px; --cxl-size: 2.5px; }
        .cxl-dot-8  { animation-delay: -2.66s;  --cxl-angle: 240deg; --cxl-dist: 49px; --cxl-size: 3px; }
        .cxl-dot-9  { animation-delay: -3s;     --cxl-angle: 270deg; --cxl-dist: 54px; --cxl-size: 2px; }
        .cxl-dot-10 { animation-delay: -3.33s;  --cxl-angle: 300deg; --cxl-dist: 47px; --cxl-size: 3px; }
        .cxl-dot-11 { animation-delay: -3.66s;  --cxl-angle: 330deg; --cxl-dist: 51px; --cxl-size: 2.5px; }

        @keyframes cxl-orbit {
          0% {
            transform: rotate(var(--cxl-angle)) translateX(var(--cxl-dist)) rotate(calc(-1 * var(--cxl-angle)));
            opacity: 0.2;
            width: var(--cxl-size);
            height: var(--cxl-size);
          }
          25% {
            opacity: 0.8;
          }
          50% {
            transform: rotate(calc(var(--cxl-angle) + 180deg)) translateX(calc(var(--cxl-dist) * 0.5)) rotate(calc(-1 * (var(--cxl-angle) + 180deg)));
            opacity: 0.5;
          }
          75% {
            opacity: 0.8;
          }
          100% {
            transform: rotate(calc(var(--cxl-angle) + 360deg)) translateX(var(--cxl-dist)) rotate(calc(-1 * (var(--cxl-angle) + 360deg)));
            opacity: 0.2;
            width: var(--cxl-size);
            height: var(--cxl-size);
          }
        }

        .cxl-particle-glow {
          position: absolute;
          top: 50%; left: 50%;
          width: 70%;
          height: 70%;
          margin-top: -35%;
          margin-left: -35%;
          border-radius: 50%;
          background: radial-gradient(circle, var(--cxl-accent-dim), transparent 70%);
          animation: cxl-ambientPulse 2.5s ease-in-out infinite;
          pointer-events: none;
        }

        /* ══════════════════════════════════════════════════════════════════
           GLOW BORDER (reused from AuthLayout for overlay card)
           ══════════════════════════════════════════════════════════════════ */
        .cxl-root .glow-border {
          position: relative;
        }
        .cxl-root .glow-border::before {
          content: '';
          position: absolute;
          inset: -2px;
          border-radius: inherit;
          padding: 2px;
          background: linear-gradient(90deg, transparent 0%, var(--cxl-accent) 25%, var(--cxl-accent2) 50%, var(--cxl-accent) 75%, transparent 100%);
          background-size: 300% 100%;
          animation: cxl-glowMove 4s ease-in-out infinite;
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          pointer-events: none;
        }
        .cxl-root .glow-border::after {
          content: '';
          position: absolute;
          inset: -8px;
          border-radius: inherit;
          background: linear-gradient(90deg, transparent 0%, var(--cxl-accent-glow) 25%, var(--cxl-accent-dim) 50%, var(--cxl-accent-glow) 75%, transparent 100%);
          background-size: 300% 100%;
          animation: cxl-glowMove 4s ease-in-out infinite, cxl-pulseGlow 2s ease-in-out infinite;
          filter: blur(12px);
          z-index: -1;
          pointer-events: none;
        }
        @keyframes cxl-glowMove {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes cxl-pulseGlow {
          0%, 100% { opacity: 0.4; filter: blur(12px); }
          50% { opacity: 0.7; filter: blur(16px); }
        }

        .cxl-root .glass-card {
          background: var(--cxl-card-bg);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid var(--cxl-card-border);
          border-radius: 16px;
        }

        /* ══ REDUCED MOTION ══ */
        @media (prefers-reduced-motion: reduce) {
          .cxl-ring, .cxl-halo, .cxl-ambient,
          .cxl-hex, .cxl-hex-glow,
          .cxl-scan-line, .cxl-scan-mask, .cxl-scan-glow,
          .cxl-dot, .cxl-particle-glow,
          .cxl-progress-bar,
          .cxl-root .glow-border::before,
          .cxl-root .glow-border::after {
            animation: none !important;
            opacity: 0.5 !important;
          }
          .cxl-progress-bar {
            left: 30% !important;
            opacity: 0.8 !important;
          }
        }
      `}</style>
    </div>
  );
}

export default CoreXLoader;
